import { currentAutosaveScope } from '@/core/api/autosaveScope'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Callout,
  FormScroll,
  FormSection,
  Icon,
  ModalHead,
  Paragraph,
  Pill,
  StatusLine,
  SubShell,
  type ModalSource,
  toastError,
  toastSuccess,
} from 'admination-react'
import {
  Tabs,
} from 'admination-react'
import { useOpenPersonOverview } from '@/core/hooks/usePhaseNav'
import { useApp } from '@/core/state/appStore'
import { currentLanguage, isLanguage, setLanguage, type Language } from '@/core/i18n'
import {
  currentPortalSession,
  endPortalSession,
  logoutToken,
  retainPortalSessionForSave,
  startPortalSession,
  type OwnPortalSession,
} from '../../../api/portal'
import { isEingeschraenkt, openTeilnehmerakte, readTeilnehmerakte } from '../../../api/teilnehmerakte-staff'
import { AUFGABE } from '../../../aufgabe'
import type { VergabeStage } from '../../../columns'
import { openedPhase, type PortalPhase } from '../portal-machine'
import { loadSession, type LoadedSession } from '../session'
import { retainedAutosave } from '../use-autosave'
import { TeilnehmerakteWizard, type WizardExit } from '../teilnehmerakte-wizard'
import type { StaffParticipant } from '@/core/state/appStore'
import { AkteViewer, GEGENZEICHNUNG_BLAETTER, staffAkteFrom, type StaffAkte } from './akte-blaetter'

const AKTE_OFFEN_AUFGABEN: ReadonlySet<string> = new Set(
  Object.values(AUFGABE)
    .filter(
      (e) =>
        e.stage === 'teilnehmerakte' &&
        e.dbName !== AUFGABE.teilnehmerakteGegenzeichnen.dbName &&
        e.dbName !== AUFGABE.potenzialanalyseAusfuellen.dbName,
    )
    .map((e) => e.dbName),
)

function akteAbgeschlossen(card: { stage: VergabeStage | string; aufgabe: string | null } | undefined): boolean {
  if (!card || card.stage === 'verbis') return false
  return card.stage !== 'teilnehmerakte' || (card.aufgabe !== null && !AKTE_OFFEN_AUFGABEN.has(card.aufgabe))
}

function dropOwnSession(owner: { current: OwnPortalSession | null }, session: OwnPortalSession): void {
  if (owner.current !== session) return
  owner.current = null
  void logoutToken(session.token, session.id)
}

function StaffWizard({
  coachingId,
  participantName,
  loaded,
  session,
  initialPhase,
  staffLanguage,
  abgeschlossen = false,
  onClosed,
  onOpenPerson,
}: {
  onOpenPerson: () => void
  coachingId: string
  participantName: string
  loaded: LoadedSession
  session: OwnPortalSession
  initialPhase: PortalPhase
  staffLanguage: Language
  abgeschlossen?: boolean
  onClosed: () => void
}) {
  const { t } = useTranslation('vergabeStaff')
  const [phase, setPhase] = useState(initialPhase)
  const loggedOut = useRef(false)

  useEffect(
    () => () => {
      if (!loggedOut.current) {
        loggedOut.current = true
        void logoutToken(session.token, session.id)
      }
      void setLanguage(staffLanguage)
    },
    [session, staffLanguage],
  )

  function exit(reason: WizardExit, pendingSave: Promise<boolean>) {
    if (!loggedOut.current) {
      loggedOut.current = true
      if (reason === 'finished') endPortalSession(session.id)
      else {
        retainPortalSessionForSave(session.id)
        void pendingSave.finally(() => logoutToken(session.token, session.id))
      }
    }
    if (reason === 'finished') toastSuccess(t('akte.finished'))
    if (reason === 'ended') toastError(t('akte.sessionEnded'))
    onClosed()
  }

  return (
    <TeilnehmerakteWizard
      personHeader={participantName ? { name: participantName, onOpen: onOpenPerson } : undefined}
      autosaveIdentity={JSON.stringify([currentAutosaveScope(), coachingId, 'teilnehmerakte'])}
      autosaveLabel={participantName}
      phase={phase}
      onNavigate={setPhase}
      loaded={loaded}
      sessionId={session.id}
      onExit={exit}
      abgeschlossen={abgeschlossen}
    />
  )
}

export function TeilnehmerakteStaffPanel({
  participant,
  source,
  onClose,
}: {
  participant: StaffParticipant
  source: ModalSource
  onClose: () => void
}) {
  const { t } = useTranslation('vergabeStaff')
  const { coachings, reloadDashboard } = useApp()
  const card = coachings.find((c) => c.id === participant.id)
  const gegenzeichnen = card?.aufgabe === AUFGABE.teilnehmerakteGegenzeichnen.dbName
  const abgeschlossen = gegenzeichnen || akteAbgeschlossen(card)
  const erstgespraech = abgeschlossen && card?.stage === 'teilnehmerakte'
  const openPerson = useOpenPersonOverview()
  const { dispatch } = useApp()
  function openErstgespraech() {
    dispatch({
      type: 'OPEN_SCREEN',
      screen: {
        kind: 'aufnahme-potenzialanalyse',
        tab: 'form',
        coachingId: participant.id,
        source: { kind: 'bar' },
      },
    })
  }
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState(false)
  const [wizard, setWizard] = useState<{
    loaded: LoadedSession
    session: OwnPortalSession
    phase: PortalPhase
    staffLanguage: Language
    abgeschlossen: boolean
  } | null>(null)
  const [akte, setAkte] = useState<StaffAkte | null>(null)
  const [reading, setReading] = useState(false)
  const readEpoch = useRef(0)
  const staffLanguage = useRef<Language>(currentLanguage())
  const mounted = useRef(true)
  const openEpoch = useRef(0)
  const ownSession = useRef<OwnPortalSession | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (ownSession.current) dropOwnSession(ownSession, ownSession.current)
      void setLanguage(staffLanguage.current)
    }
  }, [])

  async function open() {
    const epoch = ++openEpoch.current
    const scope = currentAutosaveScope()
    const stale = () => !mounted.current || openEpoch.current !== epoch || currentAutosaveScope() !== scope
    setOpening(true)
    setOpenError(false)
    staffLanguage.current = currentLanguage()
    let started: OwnPortalSession | null = null
    const retained = retainedAutosave(JSON.stringify([scope, participant.id, 'teilnehmerakte']))
    try {
      const session = await openTeilnehmerakte(participant.id)
      if (stale()) {
        void logoutToken(session.token)
        return
      }
      started = { id: startPortalSession(session), token: session.token }
      ownSession.current = started
      const loaded = await loadSession(session, started.id, retained)
      if (!stale() && isLanguage(session.sprache)) await setLanguage(session.sprache)
      if (stale() || currentPortalSession() !== started.id) {
        dropOwnSession(ownSession, started)
        return
      }
      const phase: PortalPhase = abgeschlossen
        ? { kind: 'sheet', index: 0 }
        : openedPhase(loaded)
      ownSession.current = null
      setWizard({ loaded, session: started, phase, staffLanguage: staffLanguage.current, abgeschlossen })
    } catch (error) {
      if (started) dropOwnSession(ownSession, started)
      if (stale()) return
      await setLanguage(staffLanguage.current)
      if (isEingeschraenkt(error)) toastError(t('akte.openRestricted'))
      else setOpenError(true)
    } finally {
      if (!stale()) setOpening(false)
    }
  }

  async function closeWizard() {
    if (!mounted.current) return
    setWizard(null)
    reloadDashboard()
    await setLanguage(staffLanguage.current)
  }

  async function readAkte() {
    const epoch = ++readEpoch.current
    const stale = () => !mounted.current || readEpoch.current !== epoch
    setReading(true)
    try {
      const read = staffAkteFrom(await readTeilnehmerakte(participant.id, GEGENZEICHNUNG_BLAETTER))
      if (stale()) return
      setAkte(read)
    } catch (error) {
      if (stale()) return
      if (isEingeschraenkt(error)) toastError(t('akte.openRestricted'))
      else toastError(t('akte.sheetsError'))
    } finally {
      if (!stale()) setReading(false)
    }
  }

  function closeAkte() {
    readEpoch.current++
    setReading(false)
    setAkte(null)
  }

  function countersigned() {
    toastSuccess(t('akte.allCountersigned'))
    reloadDashboard()
    onClose()
  }

  if (wizard) {
    return (
      <StaffWizard
        coachingId={participant.id}
        participantName={participant.name}
        onOpenPerson={() => openPerson(participant.id, () => dispatch({ type: 'CLOSE' }))}
        loaded={wizard.loaded}
        session={wizard.session}
        initialPhase={wizard.phase}
        staffLanguage={wizard.staffLanguage}
        abgeschlossen={wizard.abgeschlossen}
        onClosed={() => void closeWizard()}
      />
    )
  }

  if (akte) {
    return (
      <AkteViewer
        coachingId={participant.id}
        participantName={participant.name}
        akte={akte}
        reloading={reading}
        onReload={() => void readAkte()}
        onClose={closeAkte}
        onCountersigned={countersigned}
      />
    )
  }

  const header = (
    <ModalHead
      left={{ type: 'title', icon: 'i-person', label: participant.name, onClick: () => openPerson(participant.id, () => dispatch({ type: 'CLOSE' })) }}
      center={<Tabs items={[{ name: 'akte', label: t('akte.title'), icon: 'i-doc', completed: abgeschlossen }]} activeName="akte" onChange={() => {}} />}
      actions={<>
        <Pill><Button primaryAction={gegenzeichnen || !erstgespraech} iconOnly={!gegenzeichnen && erstgespraech} title={!gegenzeichnen && erstgespraech ? t(opening ? 'akte.opening' : abgeschlossen ? 'akte.view' : 'akte.open') : undefined} disabled={gegenzeichnen ? reading : opening} onClick={() => gegenzeichnen ? void readAkte() : void open()}>
          <Icon name={gegenzeichnen ? 'i-signature' : 'i-doc'} />
          {gegenzeichnen ? t(reading ? 'akte.sheetsLoading' : 'akte.countersignShow') : !erstgespraech && t(opening ? 'akte.opening' : abgeschlossen ? 'akte.view' : 'akte.open')}
        </Button></Pill>
        {erstgespraech && <Pill><Button primaryAction={!gegenzeichnen} iconOnly={gegenzeichnen} title={gegenzeichnen ? t('akte.erstgespraech') : undefined} onClick={openErstgespraech}><Icon name="i-doc" />{!gegenzeichnen && t('akte.erstgespraech')}</Button></Pill>}
      </>}
      onClose={onClose}
      closeLabel={t('akte.close')}
    />
  )

  return (
    <SubShell
      source={source}
      onClose={onClose}
      topbar={header}
    >
      <FormScroll>
        {gegenzeichnen ? (
          <>
            <FormSection>
              <StatusLine tone="success" strong icon={<Icon name="i-check" />}>
                {t('akte.allFilled')}
              </StatusLine>
            </FormSection>
            <FormSection label={t('akte.countersignTitle')}>
              <Paragraph variant="muted">{t('akte.countersignIntro')}</Paragraph>
            </FormSection>
          </>
        ) : (
          <>
          <FormSection>
            <Paragraph variant="muted">{t(abgeschlossen ? 'akte.viewHint' : 'akte.openHint')}</Paragraph>
            {openError ? (
              <Callout tone="error" role="alert">
                {t('akte.openError')}
              </Callout>
            ) : null}
          </FormSection>
          </>
        )}
      </FormScroll>
    </SubShell>
  )
}
